import type { ComponentSingleStyleConfig } from '@chakra-ui/react';

const commonLineHeights = {
  sm: '1.2',
  md: '1.3',
  lg: '1.4',
};

const fontSizes = {
  xxxs: { base: '10px', sm: '8px', md: '10px', lg: '10px', xl: '10px' },
  xxs: { base: '10px', sm: '8px', md: '10px', lg: '10px', xl: '10px' },
  xs: { base: '10px', sm: '8px', md: '9px', lg: '12px', xl: '12px' },
  sm: { base: '12px', sm: '10px', md: '11px', lg: '14px', xl: '14px' },
  md: { base: '14px', sm: '12px', md: '13px', lg: '16px', xl: '16px' },
  lg: { base: '16px', sm: '12px', md: '14px', lg: '16px', xl: '16px' },
  xl: { base: '18px', sm: '16px', md: '17px', lg: '18px', xl: '18px' },
  '2xl': { base: '20px', sm: '18px', md: '19px', lg: '20px', xl: '20px' },
  '3xl': { base: '24px', sm: '22px', md: '24px', lg: '24px', xl: '24px' },
  '4xl': { base: '32px', sm: '28px', md: '30px', lg: '32px', xl: '32px' },
  '5xl': { base: '36px', sm: '32px', md: '34px', lg: '36px', xl: '36px' },
};

const TextTheme: ComponentSingleStyleConfig = {
  baseStyle: {
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '16px',
  },
  sizes: {
    xxxs: {
      fontSize: fontSizes.xxxs,
      fontWeight: '500',
      lineHeight: commonLineHeights.sm,
    },
    xxs: {
      fontSize: fontSizes.xxs,
      fontWeight: '400',
      lineHeight: commonLineHeights.sm,
    },
    xs: {
      fontSize: fontSizes.xs,
      fontWeight: '400',
      lineHeight: commonLineHeights.sm,
    },
    sm: {
      fontSize: fontSizes.sm,
      fontWeight: '400',
      lineHeight: commonLineHeights.md,
    },
    md: {
      fontSize: fontSizes.md,
      fontWeight: '400',
      lineHeight: commonLineHeights.lg,
    },
    lg: {
      fontSize: fontSizes.lg,
      fontWeight: '500',
      lineHeight: commonLineHeights.lg,
    },
    xl: {
      fontSize: fontSizes.xl,
      fontWeight: '600',
      lineHeight: commonLineHeights.md,
    },
    '2xl': {
      fontSize: fontSizes['2xl'],
      fontWeight: '600',
      lineHeight: commonLineHeights.sm,
    },
    '3xl': {
      fontSize: fontSizes['3xl'],
      fontWeight: '700',
      lineHeight: commonLineHeights.sm,
    },
    '4xl': {
      fontSize: fontSizes['4xl'],
      fontWeight: '800',
      lineHeight: commonLineHeights.sm,
    },
    '5xl': {
      fontSize: fontSizes['5xl'],
      fontWeight: '700',
    },
  },
  variants: {
    default: {
      color: 'textBlack',
    },
    subHeading: {
      color: 'subtleSectionHeading',
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    subSectionHeading: {
      letterSpacing: '-0.02px',
      color: '#393939',
      _hover: {
        fontWeight: '600',
      },
    },
    tableText: {
      color: 'textBlack',
      textAlign: 'center',
      wordBreak: 'break-word',
      whiteSpace: 'nowrap',
    },
  },
  defaultProps: {
    variant: 'default',
    size: 'md',
  },
};

const baseTextStyles = {
  display01: {
    fontSize: '100px',
    fontWeight: 'hairline', // equivalent to extralight (100)
    lineHeight: '110%',
  },
  headline01: {
    fontSize: '54px',
    fontWeight: 'medium',
    lineHeight: '110%',
  },
  headline02: {
    fontSize: '50px',
    fontWeight: 'medium',
    lineHeight: '110%',
  },
  headline03: {
    fontSize: '32px',
    fontWeight: 'medium',
    lineHeight: '110%',
  },
  headline04: {
    fontSize: '24px',
    fontWeight: 'semibold',
    lineHeight: '110%',
  },
  body01: {
    fontSize: '14px',
    fontWeight: 'medium',
    lineHeight: '110%',
  },
  body02: {
    fontSize: '12px',
    fontWeight: 'medium',
    lineHeight: '110%',
  },
  caption: {
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: '110%',
  },
};

export const textStyles = Object.entries(baseTextStyles).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: value,
    [`${key}.avantt`]: { ...value, fontFamily: '"Avantt"' },
  }),
  {},
);

export default TextTheme;
